import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu, Globe, AlertCircle, Book, Calendar } from 'lucide-react';

const UCBerkeley: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "University of California, Berkeley";

    return (
        <div className="min-h-screen bg-white font-sans text-[#003262] selection:bg-[#FDB515] selection:text-[#003262]">
            {/* Top Bar - Berkeley Blue */}
            <div className="bg-[#003262] text-white py-2 px-6 flex justify-between items-center text-xs font-bold tracking-wide">
                <div className="flex gap-6">
                    <a href="#" className="hover:text-[#FDB515]">Gateway for...</a>
                    <a href="#" className="hover:text-[#FDB515]">Students</a>
                    <a href="#" className="hover:text-[#FDB515]">Faculty & Staff</a>
                    <a href="#" className="hover:text-[#FDB515]">Alumni</a>
                </div>
                <div className="flex gap-4">
                     <a href="#" className="hover:text-[#FDB515]">Map</a>
                     <a href="#" className="hover:text-[#FDB515]">Directory</a>
                     <a href="#" className="hover:text-[#FDB515]">bCourses</a>
                </div>
            </div>

            {/* Main Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-200">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="font-serif text-3xl md:text-4xl text-[#003262] leading-none tracking-tight">{schoolName.split(',')[0]}</span>
                        <span className="font-sans text-xs uppercase tracking-widest text-[#003262] mt-1 font-bold">{schoolName}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#003262] text-sm tracking-wide">
                        <a href="#" className="hover:underline decoration-[#FDB515] decoration-2 underline-offset-4">About</a>
                        <a href="#" className="hover:underline decoration-[#FDB515] decoration-2 underline-offset-4">Admissions</a>
                        <a href="#" className="hover:underline decoration-[#FDB515] decoration-2 underline-offset-4">Academic Programs</a>
                        <a href="#" className="hover:underline decoration-[#FDB515] decoration-2 underline-offset-4">Research</a>
                        <a href="#" className="hover:underline decoration-[#FDB515] decoration-2 underline-offset-4">Campus Life</a>
                    </div>

                    <div className="flex items-center gap-4">
                         <div className="bg-[#003262] text-white p-2 rounded-full cursor-pointer hover:bg-[#FDB515] hover:text-[#003262] transition-colors">
                            <Search size={18} />
                         </div>
                         <Menu size={28} className="text-[#003262] cursor-pointer lg:hidden" />
                    </div>
                </div>
            </nav>

            {/* Hero - Changemakers */}
            <header className="relative">
                <div className="h-[650px] relative overflow-hidden">
                    <img 
                        src="https://images.unsplash.com/photo-1599939571322-792a326991f2?q=80&w=2065&auto=format&fit=crop" 
                        alt="Campanile" 
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#003262] via-transparent to-transparent"></div>
                    
                    <div className="absolute bottom-0 left-0 w-full p-8 md:p-16 text-white">
                        <div className="max-w-[800px] mx-auto text-center">
                             <h1 className="text-5xl md:text-7xl font-bold font-serif mb-6 leading-tight">
                                 Light the Way.
                             </h1>
                             <p className="text-xl md:text-2xl mb-10 font-light text-[#FDB515]">
                                 For more than 150 years, {schoolName.split(',')[0]} has been a place where imagination leads to discovery.
                             </p>
                             <div className="flex justify-center gap-4">
                                 <Button className="bg-[#FDB515] hover:bg-[#e0a013] text-[#003262] font-bold uppercase tracking-widest px-8 h-12 rounded-full">
                                     Start Your Journey
                                 </Button>
                             </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* News & Events - Grid */}
            <section className="py-20 px-6 md:px-12 bg-[#f9f9f9]">
                <div className="max-w-[1200px] mx-auto">
                    <div className="flex items-center gap-4 mb-12">
                         <h2 className="text-3xl font-bold text-[#003262]">{schoolName.split(',')[0]} News</h2>
                         <div className="h-1 bg-[#FDB515] flex-1"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="md:col-span-2 group cursor-pointer bg-white shadow-sm hover:shadow-md transition-shadow">
                            <img src="https://images.unsplash.com/photo-1544531586-fde5298cdd40?q=80&w=2070&auto=format&fit=crop" className="w-full h-[400px] object-cover" alt="Main Story" />
                            <div className="p-8">
                                <span className="text-[#003262] font-bold uppercase text-xs tracking-widest mb-2 block">Research</span>
                                <h3 className="text-3xl font-serif font-bold text-[#003262] mb-4 group-hover:underline decoration-[#FDB515]">
                                    New genomic tool could revolutionize personalized medicine
                                </h3>
                                <p className="text-gray-600">A collaborative team of researchers has developed a faster, cheaper way to sequence DNA.</p>
                            </div>
                        </div>

                        <div className="flex flex-col gap-6">
                            {[
                                { title: "Campus welcomes record number of transfer students", date: "Oct 24" },
                                { title: "Art museum features exhibit on California landscapes", date: "Oct 22" },
                                { title: "Bears win thrilling overtime game against rival", date: "Oct 20" },
                                { title: "Nobel laureate leads panel on climate change", date: "Oct 18" }
                            ].map((item, i) => (
                                <div key={i} className="bg-white p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer group border-l-4 border-transparent hover:border-[#FDB515]">
                                     <div className="flex justify-between items-start mb-2">
                                         <span className="text-gray-400 text-xs font-bold uppercase">{item.date}</span>
                                     </div>
                                     <h4 className="text-lg font-bold text-[#003262] leading-tight group-hover:text-[#C4820E] transition-colors">{item.title}</h4>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Impact Stats */}
            <section className="bg-[#003262] text-white py-16 px-6">
                <div className="max-w-[1200px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                    <div>
                         <div className="text-5xl font-bold font-serif mb-2 text-[#FDB515]">1st</div>
                         <div className="text-sm uppercase tracking-widest font-bold">Public University</div>
                    </div>
                    <div>
                         <div className="text-5xl font-bold font-serif mb-2 text-[#FDB515]">110</div>
                         <div className="text-sm uppercase tracking-widest font-bold">Nobel Laureates</div>
                    </div>
                     <div>
                         <div className="text-5xl font-bold font-serif mb-2 text-[#FDB515]">184+</div>
                         <div className="text-sm uppercase tracking-widest font-bold">Academic Departments</div>
                    </div>
                     <div>
                         <div className="text-5xl font-bold font-serif mb-2 text-[#FDB515]">14:1</div>
                         <div className="text-sm uppercase tracking-widest font-bold">Student-Faculty Ratio</div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#0d0d0d] text-white py-16 px-6 md:px-12 border-t border-[#FDB515]">
                <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between gap-12">
                    <div className="max-w-xs text-sm text-gray-400">
                        <h2 className="text-2xl font-serif text-white mb-6">{schoolName}</h2>
                        <p className="mb-4">
                            Berkeley, CA 94720 <br/>
                            510-642-6000
                        </p>
                        <p>© 2026 {schoolName} Regents</p>
                    </div>

                    <div className="flex gap-12 font-bold text-sm text-gray-300">
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-[#FDB515]">Accessibility</a></li>
                             <li><a href="#" className="hover:text-[#FDB515]">Nondiscrimination</a></li>
                             <li><a href="#" className="hover:text-[#FDB515]">Privacy</a></li>
                         </ul>
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-[#FDB515]">Jobs</a></li>
                             <li><a href="#" className="hover:text-[#FDB515]">Media</a></li>
                             <li><a href="#" className="hover:text-[#FDB515]">Contact Us</a></li>
                         </ul>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default UCBerkeley;
